import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ethers } from "ethers";
import type { BaseStatusMessage } from "../types";
import { usePerpsOperation } from "./usePerpsOperation";
import {
  executePerpsDelegatePermission,
  executePerpsEscapeTransfer,
  executePerpsRevokePermission,
} from "../../operations/perps-operations";
import {
  PERPS_ACTION_DOMAINS,
  AVANTIS_DEFAULT_MARKET_SLIPPAGE_X10,
  computePerpsProofActionDataHash,
  encodeAvantisCancelLimitOrderCallData,
  encodeAvantisCloseTradeCallData,
  encodeAvantisOpenTradeCallData,
  encodeAvantisUpdateMarginCallData,
  computePerpsOwnershipCommitment,
  computePerpsProofActionHash,
  randomBytes32Hex,
  signPerpsActionHash,
} from "../../utils/perps-actions";
import { generatePerpsOwnershipProof } from "../../utils/perps-ownership-prover-client";
import { hashMpkHex } from "../../utils/format";
import RelayerClient from "../../utils/relayer-client";
import {
  formatInboxPublicKeyHex,
  getInboxSecretKeyCandidates,
  getInboxViewingKeyCandidates,
} from "../../utils/inbox-crypto";
import {
  buildRecoveryNotePayload,
  deriveRecoveryKeyFromMpkHex,
} from "../../utils/recovery";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import { deriveTokenHash } from "../../operations/settlement-helpers";
import { computeCommitment, computeNPK } from "../../joinsplit/keys";
import type { JoinSplitNote } from "../../joinsplit/types";
import { resolvePerpsProtocolConfig } from "../perps-protocols";
import type { PerpsMarket } from "../perps-protocols";
import {
  buildInsufficientVaultAssetMessage,
  getVaultWithdrawInsufficiencyMessage,
} from "../helpers/perpsVaultBalance";

const DEFAULT_COLLATERAL_SYNTH_MARKET_ID = (
  process.env.REACT_APP_BASE_PERPS_COLLATERAL_SYNTH_MARKET_ID || "1"
).trim();

const DEFAULT_COLLATERAL_TOKEN_ADDRESS = (
  process.env.REACT_APP_BASE_PERPS_COLLATERAL_TOKEN_ADDRESS ||
  "0x09d51516f38980035153a554c26df3c6f51a23c3"
).trim();

const DEFAULT_PERPS_ASSET_TOKEN_ADDRESS = (
  process.env.REACT_APP_BASE_PERPS_ASSET_TOKEN_ADDRESS ||
  process.env.REACT_APP_BASE_PERPS_USDC_TOKEN_ADDRESS ||
  "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"
).trim();
const DEFAULT_PERPS_ASSET_TOKEN_SYMBOL =
  (process.env.REACT_APP_BASE_PERPS_ASSET_TOKEN_SYMBOL || "USDC")
    .trim()
    .toUpperCase() || "USDC";
const DEFAULT_PERPS_ASSET_TOKEN_NAME =
  (process.env.REACT_APP_BASE_PERPS_ASSET_TOKEN_NAME || "USD Coin").trim() ||
  "USD Coin";
const DEFAULT_PERPS_ASSET_TOKEN_DECIMALS =
  Number(process.env.REACT_APP_BASE_PERPS_ASSET_TOKEN_DECIMALS || "6") || 6;

const BASE_PERPS_USDC_TOKEN_ADDRESS = (
  process.env.REACT_APP_BASE_PERPS_USDC_TOKEN_ADDRESS ||
  "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"
).trim();
const BASE_PERPS_USDT_TOKEN_ADDRESS = (
  process.env.REACT_APP_BASE_PERPS_USDT_TOKEN_ADDRESS ||
  "0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2"
).trim();
const BASE_PERPS_SUPPORTED_ASSET_LABEL = "USDC or USDT";
const BASE_PERPS_SUPPORTED_ASSET_TOKENS: TokenInfo[] = [
  {
    address: BASE_PERPS_USDC_TOKEN_ADDRESS,
    symbol: "USDC",
    name: "USD Coin",
    decimals: 6,
    isNative: false,
  },
  {
    address: BASE_PERPS_USDT_TOKEN_ADDRESS,
    symbol: "USDT",
    name: "Tether USD",
    decimals: 6,
    isNative: false,
  },
].filter((token) => ethers.isAddress(token.address));

const DEFAULT_TRADING_STORAGE_ADDRESS = (
  process.env.REACT_APP_BASE_PERPS_TRADING_STORAGE || ""
).trim();
const AVANTIS_PERPS_SELECTORS = {
  openTrade: "0x19cde9a1",
  closeTradeMarket: "0x2c3cbce4",
  cancelOpenLimitOrder: "0xb9b6573a",
  updateMargin: "0xb9fe2af3",
} as const;
const AVANTIS_EVENTS_IFACE = new ethers.Interface([
  "event MarketOrderInitiated(address indexed trader,uint256 pairIndex,bool open,uint256 orderId,uint256 timestamp,bool isBuy,bool isPnl,uint256 initialPosToken,uint256 leverage)",
  "event MarketOpenCanceled(uint256 orderId,address indexed trader,uint256 pairIndex)",
]);
const AVANTIS_MARKET_OPEN_CANCELED_TOPIC = ethers.id(
  "MarketOpenCanceled(uint256,address,uint256)",
);
const AVANTIS_MIN_INITIAL_COLLATERAL_USDC_RAW = 20_000_000n; // 20 USDC
const AVANTIS_MIN_POSITION_NOTIONAL_USD = 100;
const AVANTIS_LEVERAGE_DECIMALS = 10;
const PERPS_PROTOCOL_CONFIG = resolvePerpsProtocolConfig({
  protocolRaw: "avantis",
  collateralSynthMarketId: DEFAULT_COLLATERAL_SYNTH_MARKET_ID,
  collateralTokenAddress: DEFAULT_COLLATERAL_TOKEN_ADDRESS,
});
const PERPS_MARKETS = PERPS_PROTOCOL_CONFIG.markets;

const BPS_DENOMINATOR = 10_000n;
const INT_256_MOD = 1n << 256n;
const INT_255_MAX = (1n << 255n) - 1n;
const PERPS_PROFILE_SAVE_DEBOUNCE_MS = 1_000;
const PERPS_PROFILE_SAVE_RATE_LIMIT_BACKOFF_MS = 30_000;

type PersistedPerpsProfileSnapshot = {
  protocol: "avantis";
  vaultAddress: string;
  accountNft: string;
  accountTokenId: string;
  accountId: string;
  permissionManager: string;
  ownershipVerifier: string;
  ownershipCommitment: string;
  ownershipSalt: string;
  marketProxy: string;
  marketId: string;
  collateralAddress: string;
  marketKey: string;
  tradeDirection: "long" | "short";
  profiles?: Record<string, unknown>;
  orderPriceUsd?: string;
  orderValueUsd?: string;
  orderLeverage?: string;
};

function buildRemotePerpsProfileSnapshot(
  profile: PersistedPerpsProfileSnapshot,
): PersistedPerpsProfileSnapshot {
  return {
    protocol: profile.protocol,
    vaultAddress: profile.vaultAddress,
    accountNft: profile.accountNft,
    accountTokenId: profile.accountTokenId,
    accountId: profile.accountId,
    permissionManager: profile.permissionManager,
    ownershipVerifier: profile.ownershipVerifier,
    ownershipCommitment: profile.ownershipCommitment,
    ownershipSalt: profile.ownershipSalt,
    marketProxy: profile.marketProxy,
    marketId: profile.marketId,
    collateralAddress: profile.collateralAddress,
    marketKey: profile.marketKey,
    tradeDirection: profile.tradeDirection,
    profiles: profile.profiles,
  };
}

function normalizeSavedRemotePerpsProfile(
  saved: Record<string, unknown>,
): PersistedPerpsProfileSnapshot {
  return buildRemotePerpsProfileSnapshot({
    protocol: "avantis",
    vaultAddress: String(saved.vaultAddress || ""),
    accountNft: String(saved.accountNft || ""),
    accountTokenId: String(saved.accountTokenId || ""),
    accountId: String(saved.accountId || ""),
    permissionManager: String(saved.permissionManager || ""),
    ownershipVerifier: String(saved.ownershipVerifier || ""),
    ownershipCommitment: String(saved.ownershipCommitment || ""),
    ownershipSalt: String(saved.ownershipSalt || ""),
    marketProxy: String(saved.marketProxy || ""),
    marketId: String(saved.marketId || ""),
    collateralAddress: String(saved.collateralAddress || ""),
    marketKey: String(saved.marketKey || ""),
    tradeDirection: saved.tradeDirection === "short" ? "short" : "long",
    profiles:
      saved.profiles && typeof saved.profiles === "object"
        ? (saved.profiles as Record<string, unknown>)
        : {},
  });
}

function computeGrossTopUpForNet(
  netNeededRaw: bigint,
  totalFeeBps: bigint,
): bigint {
  if (netNeededRaw <= 0n) return 0n;
  if (totalFeeBps <= 0n) return netNeededRaw;
  if (totalFeeBps >= BPS_DENOMINATOR) return netNeededRaw;
  const retainedBps = BPS_DENOMINATOR - totalFeeBps;
  // ceil(net * denom / retained) so net-after-fees is never short.
  return (netNeededRaw * BPS_DENOMINATOR + retainedBps - 1n) / retainedBps;
}

function decodeSignedWord(hexWord: string): bigint {
  const normalized = hexWord.startsWith("0x") ? hexWord : `0x${hexWord}`;
  let value = BigInt(normalized);
  if (value > INT_255_MAX) value -= INT_256_MOD;
  return value;
}

function toDecimalNumber(value: bigint, decimals = 18): number {
  const text = ethers.formatUnits(value, decimals);
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sameAddress(a?: string | null, b?: string | null): boolean {
  return (
    !!a &&
    !!b &&
    ethers.isAddress(a) &&
    ethers.isAddress(b) &&
    a.toLowerCase() === b.toLowerCase()
  );
}

function shortAddress(address: string): string {
  return ethers.isAddress(address)
    ? `${address.slice(0, 6)}...${address.slice(-4)}`
    : address;
}

function addressLogTopic(address: string): string {
  return ethers.zeroPadValue(ethers.getAddress(address), 32).toLowerCase();
}

function isNonZeroBytes32Result(raw: string): boolean {
  return raw !== "0x" && !/^0x0*$/i.test(raw);
}

function parseAvantisLog(log: {
  topics: readonly string[];
  data: string;
}): ethers.LogDescription | null {
  try {
    return AVANTIS_EVENTS_IFACE.parseLog({
      topics: [...log.topics],
      data: log.data,
    });
  } catch {
    return null;
  }
}

function getInitiatedAvantisOrder(
  receipt: ethers.TransactionReceipt,
  vaultAddress: string,
): { orderId: string; pairIndex: bigint; isOpen: boolean } | null {
  for (const log of receipt.logs) {
    const parsed = parseAvantisLog(log);
    if (!parsed || parsed.name !== "MarketOrderInitiated") continue;
    const trader = String(parsed.args[0]);
    const isOpen = Boolean(parsed.args[2]);
    if (!sameAddress(trader, vaultAddress)) continue;
    return {
      orderId: parsed.args[3].toString(),
      pairIndex: BigInt(parsed.args[1].toString()),
      isOpen,
    };
  }
  return null;
}

type AvantisActiveTradeSummary = {
  side: "long" | "short";
  collateralRaw: bigint;
  collateralUsd: number;
  leverage: number;
  notionalUsd: number;
  baseSize: number | null;
  entryPriceUsd: number | null;
};

async function readAvantisOpenTrade(params: {
  provider: ethers.Provider;
  tradingStorageAddress: string;
  vaultAddress: string;
  pairIndex: bigint;
  tradeIndex: bigint;
  collateralDecimals: number;
}): Promise<AvantisActiveTradeSummary | null> {
  const iface = new ethers.Interface([
    "function openTrades(address trader,uint256 pairIndex,uint256 tradeIndex) view returns ((address trader,uint256 pairIndex,uint256 index,uint256 initialPosToken,uint256 positionSizeUSDC,uint256 openPrice,bool buy,uint256 leverage,uint256 tp,uint256 sl,uint256 timestamp))",
  ]);
  const raw = await params.provider.call({
    to: params.tradingStorageAddress,
    data: iface.encodeFunctionData("openTrades", [
      params.vaultAddress,
      params.pairIndex,
      params.tradeIndex,
    ]),
  });
  if (!isNonZeroBytes32Result(raw)) return null;
  const decoded = iface.decodeFunctionResult("openTrades", raw);
  const trade = decoded[0];
  const trader = String(trade.trader ?? trade[0]);
  if (!sameAddress(trader, params.vaultAddress)) return null;
  const collateralRaw = BigInt((trade.initialPosToken ?? trade[3]).toString());
  if (collateralRaw <= 0n) return null;
  const leverageRaw = BigInt((trade.leverage ?? trade[7]).toString());
  const openPriceRaw = BigInt((trade.openPrice ?? trade[5]).toString());
  const collateralUsd = toDecimalNumber(
    collateralRaw,
    params.collateralDecimals,
  );
  const leverage = toDecimalNumber(leverageRaw, AVANTIS_LEVERAGE_DECIMALS);
  const entryPriceUsd =
    openPriceRaw > 0n ? toDecimalNumber(openPriceRaw, 10) : null;
  const notionalUsd = collateralUsd * leverage;
  const baseSize =
    entryPriceUsd && entryPriceUsd > 0 ? notionalUsd / entryPriceUsd : null;
  return {
    side: Boolean(trade.buy ?? trade[6]) ? "long" : "short",
    collateralRaw,
    collateralUsd,
    leverage,
    notionalUsd,
    baseSize,
    entryPriceUsd,
  };
}

async function readAvantisPendingOrderIds(params: {
  provider: ethers.Provider;
  tradingStorageAddress: string;
  vaultAddress: string;
}): Promise<string[]> {
  const iface = new ethers.Interface([
    "function getPendingOrderIds(address trader) view returns (uint256[])",
  ]);
  const raw = await params.provider.call({
    to: params.tradingStorageAddress,
    data: iface.encodeFunctionData("getPendingOrderIds", [params.vaultAddress]),
  });
  const decoded = iface.decodeFunctionResult("getPendingOrderIds", raw);
  return Array.from(decoded[0] as bigint[]).map((id) => id.toString());
}

function parsePositiveDecimalInputRaw(
  value: string,
  decimals: number,
): bigint | null {
  const numeric = Number(value || "0");
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  try {
    return ethers.parseUnits(numeric.toFixed(decimals), decimals);
  } catch {
    return null;
  }
}

function computeAvantisCollateralInTradeUsdcRaw(params: {
  orderValueUsd: string;
  leverageX10: bigint;
  perpsAssetDecimals: number;
}): bigint | null {
  if (params.leverageX10 <= 0n) return null;
  const notionalRaw = parsePositiveDecimalInputRaw(
    params.orderValueUsd,
    params.perpsAssetDecimals,
  );
  if (!notionalRaw || notionalRaw <= 0n) return null;
  const leverageScale = 10n ** BigInt(AVANTIS_LEVERAGE_DECIMALS);
  return (
    (notionalRaw * leverageScale + params.leverageX10 - 1n) / params.leverageX10
  );
}

async function waitMs(ms: number): Promise<void> {
  await new Promise<void>((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

function resolveSupportedBasePerpsAssetToken(
  address: string,
  configured?: TokenInfo | null,
): TokenInfo | null {
  if (!ethers.isAddress(address)) return null;
  if (configured && sameAddress(address, configured.address)) return configured;
  return (
    BASE_PERPS_SUPPORTED_ASSET_TOKENS.find((token) =>
      sameAddress(address, token.address),
    ) || null
  );
}

function resolveAvantisPairIndex(
  rawMarketId: string,
  marketKey: string,
): bigint {
  const parsed = Number(rawMarketId);
  if (Number.isFinite(parsed) && (parsed === 0 || parsed === 1)) {
    return BigInt(parsed);
  }
  if (marketKey === "eth") return 0n;
  if (marketKey === "btc") return 1n;
  return 0n;
}

type WalletShape = {
  address: string;
  signer: ethers.Signer | null;
  // `publicKey` is the Baby Jubjub pair the perps ownership commitment names
  // and the action signature is verified against.
  spendingKeys: {
    mpk: bigint;
    publicKey: [bigint, bigint];
    spendingKey?: Uint8Array;
  } | null;
};

type UsePerpsTabControllerParams = {
  wallet: WalletShape;
  chainId: number;
  setStatus: (status: BaseStatusMessage | null) => void;
  setLoading: (loading: boolean) => void;
  addLog: (
    message: string,
    type?: "info" | "success" | "error" | "warning",
    txHash?: string,
  ) => void;
  configuredPerpsAssetToken: TokenInfo | null;
  ensureWalletCompliant: () => boolean;
  authHeadersForPath: (path: string) => Promise<Record<string, string>>;
  fundVaultFromPrivateAsset?: (params: {
    vaultAddress: string;
    amountRaw: bigint;
    token: TokenInfo;
  }) => Promise<boolean>;
  shieldWalletAssetToPrivate?: (params: {
    amountRaw: bigint;
    token: TokenInfo;
  }) => Promise<boolean>;
  ingestPerpsWithdrawPrivateNote?: (params: {
    note: JoinSplitNote;
    txHash?: string;
    amountRaw: bigint;
    token: TokenInfo;
  }) => Promise<boolean>;
};

type PerpsPendingAction =
  | "createAccount"
  | "commitOrder"
  | "closePosition"
  | "cancelOrder"
  | "fundVault"
  | "withdrawVault"
  | "depositMargin"
  | "withdrawMargin"
  | "delegate"
  | "revoke"
  | "escape";

type PerpsSelectorSupport = {
  openTrade: boolean;
  closeTradeMarket: boolean;
  cancelOpenLimitOrder: boolean;
  updateMargin: boolean;
};

export function usePerpsTabController({
  wallet,
  chainId,
  setStatus,
  setLoading,
  addLog,
  configuredPerpsAssetToken,
  ensureWalletCompliant,
  authHeadersForPath,
  fundVaultFromPrivateAsset,
  shieldWalletAssetToPrivate,
  ingestPerpsWithdrawPrivateNote,
}: UsePerpsTabControllerParams) {
  const [vaultOwner, setVaultOwner] = useState("");
  const [accountNft, setAccountNft] = useState("");
  const [accountTokenId, setAccountTokenId] = useState("0");
  const [accountId, setAccountId] = useState("0");
  const [permissionManager, setPermissionManager] = useState("");
  const [ownershipVerifier, setOwnershipVerifier] = useState("");
  const [ownershipCommitment, setOwnershipCommitment] = useState("");
  const [ownershipSalt, setOwnershipSalt] = useState("");
  const [perpsMarketProxy, setPerpsMarketProxy] = useState("");
  const [vaultAddress, setVaultAddress] = useState("");
  const [executor, setExecutor] = useState("");
  const [permission, setPermission] = useState(ethers.id("PERPS_TRADE"));
  const [expiry, setExpiry] = useState(
    String(Math.floor(Date.now() / 1000) + 3600),
  );
  const [ownershipProof, setOwnershipProof] = useState("");
  const [escapeRecipient, setEscapeRecipient] = useState("");
  const [traderMarketId, setTraderMarketId] = useState(
    PERPS_MARKETS[0].marketId,
  );
  const [traderCollateralAddress, setTraderCollateralAddress] = useState("0");
  const [traderMarginAmount, setTraderMarginAmount] = useState("0");
  const [traderOrderSizeDelta, setTraderOrderSizeDelta] = useState("0");
  const [traderOrderSettlementStrategyId, setTraderOrderSettlementStrategyId] =
    useState("0");
  const [traderOrderAcceptablePrice, setTraderOrderAcceptablePrice] =
    useState("0");
  const [traderOrderTrackingCode, setTraderOrderTrackingCode] = useState(
    ethers.ZeroHash,
  );
  const [traderOrderReferrer, setTraderOrderReferrer] = useState(
    ethers.ZeroAddress,
  );
  const [selectedMarketKey, setSelectedMarketKey] = useState(
    PERPS_MARKETS[0].key,
  );
  const [tradeDirection, setTradeDirection] = useState<"long" | "short">(
    "long",
  );
  const [orderPriceUsd, setOrderPriceUsd] = useState(
    PERPS_MARKETS[0].defaultPriceUsd,
  );
  const [orderValueUsd, setOrderValueUsd] = useState("200");
  const [orderLeverage, setOrderLeverage] = useState("5");
  const perpsProfileSaveTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const lastSavedRemotePerpsProfileRef = useRef("");
  const remotePerpsProfileBackoffUntilRef = useRef(0);
  const remotePerpsProfileRateLimitNoticeAtRef = useRef(0);
  const lastSelectedMarketKeyRef = useRef(PERPS_MARKETS[0].key);
  const lastSelectedMarketDefaultPriceRef = useRef(
    PERPS_MARKETS[0].defaultPriceUsd,
  );
  const [readinessChecking, setReadinessChecking] = useState(false);
  const [ready, setReady] = useState(false);
  const [readinessMessage, setReadinessMessage] = useState(
    "Create account to initialize readiness checks.",
  );
  const [perpsSelectorSupport, setPerpsSelectorSupport] =
    useState<PerpsSelectorSupport | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<PerpsPendingAction | null>(
    null,
  );
  const [vaultAssetBalanceUsd, setVaultAssetBalanceUsd] = useState<
    number | null
  >(null);
  const [vaultAssetBalanceRaw, setVaultAssetBalanceRaw] = useState<
    bigint | null
  >(null);
  const [hasActiveTrade, setHasActiveTrade] = useState(false);
  const [activeTradeSummary, setActiveTradeSummary] =
    useState<AvantisActiveTradeSummary | null>(null);
  const [pendingMarketOrderIds, setPendingMarketOrderIds] = useState<string[]>(
    [],
  );
  const [availableMarginUsd, setAvailableMarginUsd] = useState<number | null>(
    null,
  );
  const [withdrawableMarginUsd, setWithdrawableMarginUsd] = useState<
    number | null
  >(null);
  const [totalCollateralUsd, setTotalCollateralUsd] = useState<number | null>(
    null,
  );
  const [openPositionSize, setOpenPositionSize] = useState<number | null>(null);
  const [unrealizedPnlUsd, setUnrealizedPnlUsd] = useState<number | null>(null);
  const [recipientKey, setRecipientKey] = useState("");
  const [persistedProfileScopes, setPersistedProfileScopes] = useState<
    Record<string, unknown>
  >({});
  const [resolvedPerpsAssetToken, setResolvedPerpsAssetToken] =
    useState<TokenInfo | null>(null);
  const [perpsAccountAssetMismatch, setPerpsAccountAssetMismatch] =
    useState(false);
  const hasPerpsAccount =
    ethers.isAddress(vaultAddress) && !perpsAccountAssetMismatch;

  const {
    createPerpsAccount,
    submitPerpsTrade,
    submitPerpsMargin,
    submitPerpsVaultWithdraw,
  } = usePerpsOperation({
    chainId,
    addLog,
    setStatus,
    setLoading,
  });

  useEffect(() => {
    if (!wallet.address) return;
    if (!vaultOwner) setVaultOwner(wallet.address);
    if (!escapeRecipient) setEscapeRecipient(wallet.address);
  }, [wallet.address, vaultOwner, escapeRecipient]);

  useEffect(() => {
    if (!accountNft)
      setAccountNft(process.env.REACT_APP_BASE_PERPS_ACCOUNT_NFT || "");
    if (!permissionManager)
      setPermissionManager(
        process.env.REACT_APP_BASE_PERPS_PERMISSION_MANAGER || "",
      );
    if (!ownershipVerifier)
      setOwnershipVerifier(
        process.env.REACT_APP_BASE_PERPS_OWNERSHIP_VERIFIER ||
          "0xDeD4B9dB6Ed930eA2F6ac58689124BA4Da696909",
      );
    if (!perpsMarketProxy)
      setPerpsMarketProxy(process.env.REACT_APP_BASE_PERPS_MARKET_PROXY || "");
  }, [accountNft, permissionManager, ownershipVerifier, perpsMarketProxy]);

  const selectedMarket = useMemo(
    () =>
      PERPS_MARKETS.find((market) => market.key === selectedMarketKey) ||
      PERPS_MARKETS[0],
    [selectedMarketKey],
  );
  const activePerpsAssetToken = useMemo<TokenInfo | null>(
    () =>
      (perpsAccountAssetMismatch ? null : resolvedPerpsAssetToken) ||
      configuredPerpsAssetToken ||
      (ethers.isAddress(DEFAULT_PERPS_ASSET_TOKEN_ADDRESS)
        ? {
            address: DEFAULT_PERPS_ASSET_TOKEN_ADDRESS,
            symbol: DEFAULT_PERPS_ASSET_TOKEN_SYMBOL,
            name: DEFAULT_PERPS_ASSET_TOKEN_NAME,
            decimals: DEFAULT_PERPS_ASSET_TOKEN_DECIMALS,
            isNative: false,
          }
        : null),
    [
      resolvedPerpsAssetToken,
      configuredPerpsAssetToken,
      perpsAccountAssetMismatch,
    ],
  );
  const perpsAssetAddress =
    activePerpsAssetToken?.address ?? DEFAULT_PERPS_ASSET_TOKEN_ADDRESS;
  const perpsAssetDecimals =
    activePerpsAssetToken?.decimals ?? DEFAULT_PERPS_ASSET_TOKEN_DECIMALS;
  const perpsAssetSymbol =
    activePerpsAssetToken?.symbol || DEFAULT_PERPS_ASSET_TOKEN_SYMBOL;

  const vaultWithdrawPreflightMessage = useMemo(() => {
    const amountInput = (traderMarginAmount || "").trim();
    if (!amountInput) return null;
    let requestedRaw: bigint;
    try {
      requestedRaw = ethers.parseUnits(amountInput, perpsAssetDecimals);
    } catch {
      return null;
    }
    return getVaultWithdrawInsufficiencyMessage({
      requestedRaw,
      availableRaw: vaultAssetBalanceRaw,
      decimals: perpsAssetDecimals,
      symbol: perpsAssetSymbol,
    });
  }, [
    traderMarginAmount,
    vaultAssetBalanceRaw,
    perpsAssetDecimals,
    perpsAssetSymbol,
  ]);

  useEffect(() => {
    setTraderMarketId(selectedMarket.marketId);
    setTraderCollateralAddress(selectedMarket.synthMarketId);
    const previousMarketKey = lastSelectedMarketKeyRef.current;
    if (previousMarketKey !== selectedMarket.key) {
      const previousDefaultPrice = lastSelectedMarketDefaultPriceRef.current;
      const currentPrice = orderPriceUsd.trim();
      if (!currentPrice || currentPrice === previousDefaultPrice) {
        setOrderPriceUsd(selectedMarket.defaultPriceUsd);
      }
      lastSelectedMarketKeyRef.current = selectedMarket.key;
      lastSelectedMarketDefaultPriceRef.current =
        selectedMarket.defaultPriceUsd;
    }
  }, [selectedMarket, orderPriceUsd]);

  useEffect(() => {
    const currentPrice = orderPriceUsd.trim();
    if (!currentPrice || currentPrice === "0") {
      setTraderOrderAcceptablePrice("0");
      setTraderOrderSizeDelta("0");
      return;
    }
    const parsedPrice = Number(currentPrice);
    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      setTraderOrderAcceptablePrice("0");
      setTraderOrderSizeDelta("0");
      return;
    }
    const parsedValue = Number(orderValueUsd);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      setTraderOrderSizeDelta("0");
    } else {
      const baseSize = parsedValue / parsedPrice;
      const sizeWei = ethers.parseUnits(baseSize.toFixed(18), 18);
      const signedSize = tradeDirection === "long" ? sizeWei : -sizeWei;
      setTraderOrderSizeDelta(signedSize.toString());
    }
    setTraderOrderAcceptablePrice(
      ethers.parseUnits(parsedPrice.toFixed(6), 18).toString(),
    );
  }, [orderPriceUsd, orderValueUsd, tradeDirection]);

  const resolveVaultPerpsAssetToken = useCallback(async () => {
    if (!wallet.signer?.provider || !ethers.isAddress(vaultAddress)) {
      setResolvedPerpsAssetToken(null);
      setPerpsAccountAssetMismatch(false);
      return;
    }
    try {
      const provider = wallet.signer.provider;
      const vault = new ethers.Contract(
        vaultAddress,
        ["function collateralToken() view returns (address)"],
        provider,
      );
      const assetAddressRaw = String(await vault.collateralToken());
      if (
        !ethers.isAddress(assetAddressRaw) ||
        assetAddressRaw === ethers.ZeroAddress
      ) {
        setResolvedPerpsAssetToken(null);
        setPerpsAccountAssetMismatch(false);
        return;
      }
      const supportedToken = resolveSupportedBasePerpsAssetToken(
        assetAddressRaw,
        configuredPerpsAssetToken,
      );
      if (!supportedToken) {
        setResolvedPerpsAssetToken(null);
        setPerpsAccountAssetMismatch(true);
        setReady(false);
        setReadinessMessage(
          `Saved perps vault uses unsupported collateral ${shortAddress(assetAddressRaw)}. Base perps supports ${BASE_PERPS_SUPPORTED_ASSET_LABEL}.`,
        );
        return;
      }
      setPerpsAccountAssetMismatch(false);
      setResolvedPerpsAssetToken(supportedToken);
      return;
    } catch {
      setResolvedPerpsAssetToken(null);
      setPerpsAccountAssetMismatch(false);
    }
  }, [wallet.signer, vaultAddress, configuredPerpsAssetToken]);

  useEffect(() => {
    void resolveVaultPerpsAssetToken();
  }, [resolveVaultPerpsAssetToken]);

  const storageKey = useMemo(
    () =>
      wallet.address ? `stealthos:perps:${wallet.address.toLowerCase()}` : "",
    [wallet.address],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!wallet.spendingKeys) {
        setRecipientKey("");
        return;
      }
      const mpkHex = `0x${wallet.spendingKeys.mpk.toString(16).padStart(64, "0")}`;
      const key = await hashMpkHex(mpkHex);
      if (!cancelled) setRecipientKey(key.toLowerCase());
    })();
    return () => {
      cancelled = true;
    };
  }, [wallet.spendingKeys]);

  const applyPersistedProfile = useCallback(
    (saved: Record<string, string>) => {
      if (saved.vaultAddress) setVaultAddress(saved.vaultAddress);
      if (saved.accountNft) setAccountNft(saved.accountNft);
      if (saved.accountTokenId) setAccountTokenId(saved.accountTokenId);
      if (saved.accountId) setAccountId(saved.accountId);
      if (saved.permissionManager)
        setPermissionManager(saved.permissionManager);
      if (saved.ownershipVerifier)
        setOwnershipVerifier(saved.ownershipVerifier);
      if (saved.ownershipCommitment)
        setOwnershipCommitment(saved.ownershipCommitment);
      if (saved.ownershipSalt) setOwnershipSalt(saved.ownershipSalt);
      if (saved.marketProxy) {
        const savedProxy = String(saved.marketProxy).trim();
        setPerpsMarketProxy(savedProxy);
      }
      if (saved.marketId) {
        const candidate = String(saved.marketId).trim();
        const isKnownMarketId = PERPS_MARKETS.some(
          (m) => m.marketId === candidate,
        );
        if (isKnownMarketId) {
          setTraderMarketId(candidate);
        }
      }
      if (saved.collateralAddress) {
        const raw = String(saved.collateralAddress).trim();
        // Legacy profiles stored ERC20 addresses; collateral now uses synthMarketId (uint128).
        const normalized = raw.startsWith("0x") ? "" : raw;
        if (
          /^\d+$/.test(normalized) &&
          normalized === DEFAULT_COLLATERAL_SYNTH_MARKET_ID
        ) {
          setTraderCollateralAddress(normalized);
        } else {
          // Prevent stale persisted market ids (e.g. removed markets) from breaking deposits.
          setTraderCollateralAddress(DEFAULT_COLLATERAL_SYNTH_MARKET_ID);
        }
      }
      if (saved.marketKey) setSelectedMarketKey(saved.marketKey);
      if (saved.orderPriceUsd) setOrderPriceUsd(saved.orderPriceUsd);
      if (saved.orderValueUsd) setOrderValueUsd(saved.orderValueUsd);
      if (saved.orderLeverage) setOrderLeverage(saved.orderLeverage);
      if (saved.tradeDirection === "long" || saved.tradeDirection === "short") {
        setTradeDirection(saved.tradeDirection);
      }
    },
    [addLog],
  );

  const localPersistedPerpsProfile = useMemo<PersistedPerpsProfileSnapshot>(
    () => ({
      protocol: "avantis",
      vaultAddress,
      accountNft,
      accountTokenId,
      accountId,
      permissionManager,
      ownershipVerifier,
      ownershipCommitment,
      ownershipSalt,
      marketProxy: perpsMarketProxy,
      marketId: traderMarketId,
      collateralAddress: traderCollateralAddress,
      marketKey: selectedMarketKey,
      orderPriceUsd,
      orderValueUsd,
      orderLeverage,
      tradeDirection,
      profiles: persistedProfileScopes,
    }),
    [
      vaultAddress,
      accountNft,
      accountTokenId,
      accountId,
      permissionManager,
      ownershipVerifier,
      ownershipCommitment,
      ownershipSalt,
      perpsMarketProxy,
      traderMarketId,
      traderCollateralAddress,
      selectedMarketKey,
      orderPriceUsd,
      orderValueUsd,
      orderLeverage,
      tradeDirection,
      persistedProfileScopes,
    ],
  );

  const remotePersistedPerpsProfile = useMemo<PersistedPerpsProfileSnapshot>(
    () => buildRemotePerpsProfileSnapshot(localPersistedPerpsProfile),
    [localPersistedPerpsProfile],
  );

  useEffect(() => {
    if (perpsProfileSaveTimeoutRef.current) {
      clearTimeout(perpsProfileSaveTimeoutRef.current);
      perpsProfileSaveTimeoutRef.current = null;
    }
    lastSavedRemotePerpsProfileRef.current = "";
    remotePerpsProfileBackoffUntilRef.current = 0;
    remotePerpsProfileRateLimitNoticeAtRef.current = 0;
  }, [recipientKey]);

  useEffect(() => {
    if (!recipientKey) return;
    let cancelled = false;
    void (async () => {
      try {
        if (!wallet.spendingKeys?.spendingKey) return;
        const authHeaders = await authHeadersForPath("/api/v1/perps/account");
        const mpkHex = `0x${wallet.spendingKeys.mpk.toString(16).padStart(64, "0")}`;
        const loaded = await RelayerClient.loadPerpsAccountProfile(
          recipientKey,
          authHeaders,
          {
            viewingKeyHex: getInboxViewingKeyCandidates(
              wallet.spendingKeys.spendingKey,
            ),
            legacyMpkHex: mpkHex,
            inboxSecretKey: getInboxSecretKeyCandidates(
              wallet.spendingKeys.spendingKey,
            ),
          },
        );
        if (cancelled) return;
        if (loaded.found && loaded.profile) {
          setPersistedProfileScopes(
            loaded.profile &&
              typeof loaded.profile === "object" &&
              loaded.profile.profiles
              ? loaded.profile.profiles
              : {},
          );
          lastSavedRemotePerpsProfileRef.current = JSON.stringify(
            normalizeSavedRemotePerpsProfile(
              loaded.profile as Record<string, unknown>,
            ),
          );
          applyPersistedProfile(loaded.profile as Record<string, string>);
          return;
        }
      } catch (err) {
        addLog(
          `Perps profile Redis load fallback: ${err instanceof Error ? err.message : String(err)}`,
          "warning",
        );
      }
      if (typeof window === "undefined" || !storageKey) return;
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      try {
        setPersistedProfileScopes({});
        const saved = JSON.parse(raw) as Record<string, string>;
        applyPersistedProfile(saved);
      } catch {
        // ignore malformed local cache
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    recipientKey,
    storageKey,
    applyPersistedProfile,
    addLog,
    authHeadersForPath,
    wallet.spendingKeys,
  ]);

  useEffect(() => {
    if (typeof window === "undefined" || !storageKey) return;
    if (
      !localPersistedPerpsProfile.vaultAddress &&
      !localPersistedPerpsProfile.ownershipCommitment
    ) {
      return;
    }
    window.localStorage.setItem(
      storageKey,
      JSON.stringify(localPersistedPerpsProfile),
    );
  }, [storageKey, localPersistedPerpsProfile]);

  useEffect(() => {
    if (!recipientKey) return;
    if (
      !remotePersistedPerpsProfile.vaultAddress &&
      !remotePersistedPerpsProfile.ownershipCommitment
    ) {
      return;
    }
    const profileJson = JSON.stringify(remotePersistedPerpsProfile);
    if (profileJson === lastSavedRemotePerpsProfileRef.current) return;
    if (perpsProfileSaveTimeoutRef.current) {
      clearTimeout(perpsProfileSaveTimeoutRef.current);
    }
    perpsProfileSaveTimeoutRef.current = setTimeout(() => {
      perpsProfileSaveTimeoutRef.current = null;
      if (profileJson === lastSavedRemotePerpsProfileRef.current) return;
      if (Date.now() < remotePerpsProfileBackoffUntilRef.current) return;
      void (async () => {
        try {
          if (!wallet.spendingKeys?.spendingKey) return;
          const authHeaders = await authHeadersForPath("/api/v1/perps/account");
          await RelayerClient.savePerpsAccountProfile(
            recipientKey,
            remotePersistedPerpsProfile,
            authHeaders,
            {
              inboxPublicKeyHex: formatInboxPublicKeyHex(
                wallet.spendingKeys.spendingKey,
              ),
            },
          );
          lastSavedRemotePerpsProfileRef.current = profileJson;
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          if (/429|too many requests/i.test(message)) {
            remotePerpsProfileBackoffUntilRef.current =
              Date.now() + PERPS_PROFILE_SAVE_RATE_LIMIT_BACKOFF_MS;
            if (
              Date.now() - remotePerpsProfileRateLimitNoticeAtRef.current >
              PERPS_PROFILE_SAVE_RATE_LIMIT_BACKOFF_MS
            ) {
              remotePerpsProfileRateLimitNoticeAtRef.current = Date.now();
              addLog(
                "Perps profile sync is being rate limited. Backing off briefly before retrying.",
                "warning",
              );
            }
            return;
          }
          addLog(`Perps profile Redis save failed: ${message}`, "warning");
        }
      })();
    }, PERPS_PROFILE_SAVE_DEBOUNCE_MS);
    return () => {
      if (perpsProfileSaveTimeoutRef.current) {
        clearTimeout(perpsProfileSaveTimeoutRef.current);
        perpsProfileSaveTimeoutRef.current = null;
      }
    };
  }, [
    recipientKey,
    remotePersistedPerpsProfile,
    addLog,
    authHeadersForPath,
    wallet.spendingKeys,
  ]);

  const derivedTrade = useMemo(() => {
    const price = Number(orderPriceUsd);
    const value = Number(orderValueUsd);
    const leverage = Number(orderLeverage);
    const validPrice = Number.isFinite(price) && price > 0 ? price : 0;
    const validValue = Number.isFinite(value) && value > 0 ? value : 0;
    const validLeverage =
      Number.isFinite(leverage) && leverage > 0 ? leverage : 0;
    const baseSize = validPrice > 0 ? validValue / validPrice : 0;
    const requiredMarginUsd =
      validLeverage > 0 ? validValue / validLeverage : 0;
    const recommendedMarginInput =
      requiredMarginUsd > 0 ? requiredMarginUsd.toFixed(2) : "0";
    return {
      notionalUsd: validValue,
      baseSize,
      leverage: validLeverage,
      requiredMarginUsd,
      recommendedMarginInput,
    };
  }, [orderPriceUsd, orderValueUsd, orderLeverage]);

  const chartEmbedUrl = useMemo(() => {
    const template =
      EVM_CONFIG.BASE_PERPS_CHART_EMBED_URL ||
      process.env.REACT_APP_BASE_PERPS_CHART_EMBED_URL ||
      "";
    if (template) {
      return template
        .split("{symbol}")
        .join(encodeURIComponent(selectedMarket.chartSymbol))
        .split("{market}")
        .join(encodeURIComponent(selectedMarket.label));
    }
    return `https://s.tradingview.com/widgetembed/?symbol=${encodeURIComponent(selectedMarket.chartSymbol)}&interval=15&theme=dark&style=1&toolbarbg=0b0d1a&withdateranges=1&hide_side_toolbar=0&allow_symbol_change=0`;
  }, [selectedMarket.chartSymbol, selectedMarket.label]);

  const refreshReadiness = useCallback(
    async (override?: {
      vaultAddress?: string;
      perpsMarketProxy?: string;
      ownershipCommitment?: string;
    }) => {
      if (!wallet.signer?.provider) return;
      const targetVaultAddress = override?.vaultAddress || vaultAddress;
      const targetPerpsMarketProxy =
        override?.perpsMarketProxy || perpsMarketProxy;
      const targetOwnershipCommitment =
        override?.ownershipCommitment || ownershipCommitment;
      if (
        !ethers.isAddress(targetVaultAddress) ||
        !ethers.isAddress(targetPerpsMarketProxy) ||
        !targetOwnershipCommitment
      ) {
        setReady(false);
        setPerpsSelectorSupport(null);
        setReadinessMessage("Missing Avantis vault/proxy/commitment config.");
        return;
      }
      try {
        setReadinessChecking(true);
        const provider = wallet.signer.provider;
        const vaultCode = await provider.getCode(targetVaultAddress);
        if (!vaultCode || vaultCode === "0x") {
          setReady(false);
          setPerpsSelectorSupport(null);
          setReadinessMessage(
            "Finalizing private perps account. This can take a few seconds.",
          );
          return;
        }
        const vault = new ethers.Contract(
          targetVaultAddress,
          [
            "function perpsMarketProxy() view returns (address)",
            "function collateralToken() view returns (address)",
            "function ownershipCommitment() view returns (bytes32)",
            "function allowedPerpsSelectors(bytes4) view returns (bool)",
          ],
          provider,
        );
        const [vaultMarketProxy, vaultCollateralToken, vaultCommitment] =
          await Promise.all([
            vault.perpsMarketProxy(),
            vault.collateralToken(),
            vault.ownershipCommitment(),
          ]);
        const proxyOk =
          String(vaultMarketProxy).toLowerCase() ===
          targetPerpsMarketProxy.toLowerCase();
        const collateralToken = String(vaultCollateralToken);
        const collateralOk = !!resolveSupportedBasePerpsAssetToken(
          collateralToken,
          configuredPerpsAssetToken,
        );
        const commitmentOk =
          String(vaultCommitment).toLowerCase() ===
          targetOwnershipCommitment.toLowerCase();
        const selectorSupport: PerpsSelectorSupport = {
          openTrade: Boolean(
            await vault.allowedPerpsSelectors(
              AVANTIS_PERPS_SELECTORS.openTrade,
            ),
          ),
          closeTradeMarket: Boolean(
            await vault.allowedPerpsSelectors(
              AVANTIS_PERPS_SELECTORS.closeTradeMarket,
            ),
          ),
          cancelOpenLimitOrder: Boolean(
            await vault.allowedPerpsSelectors(
              AVANTIS_PERPS_SELECTORS.cancelOpenLimitOrder,
            ),
          ),
          updateMargin: Boolean(
            await vault.allowedPerpsSelectors(
              AVANTIS_PERPS_SELECTORS.updateMargin,
            ),
          ),
        };
        setPerpsSelectorSupport(selectorSupport);
        const requiredSelectorsOk =
          selectorSupport.openTrade && selectorSupport.closeTradeMarket;
        if (!collateralOk) {
          setPerpsAccountAssetMismatch(true);
          setResolvedPerpsAssetToken(null);
        }
        if (proxyOk && collateralOk && commitmentOk && requiredSelectorsOk) {
          setPerpsAccountAssetMismatch(false);
          setReady(true);
          if (
            selectorSupport.cancelOpenLimitOrder &&
            selectorSupport.updateMargin
          ) {
            setReadinessMessage("Ready: private perps account verified.");
          } else {
            setReadinessMessage(
              "Ready for open/close. Legacy vault selector set: cancel and margin updates are unavailable on this vault.",
            );
          }
        } else {
          setReady(false);
          if (!collateralOk) {
            setReadinessMessage(
              `Not ready: vault collateral ${shortAddress(collateralToken)} is unsupported. Base perps supports ${BASE_PERPS_SUPPORTED_ASSET_LABEL}.`,
            );
          } else if (!requiredSelectorsOk) {
            setReadinessMessage(
              "Not ready: vault is missing required Avantis open/close selectors.",
            );
          } else {
            setReadinessMessage(
              `Not ready: proxy=${proxyOk} commitment=${commitmentOk}`,
            );
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setReady(false);
        setPerpsSelectorSupport(null);
        if (
          msg.includes("could not decode result data") ||
          msg.includes("BAD_DATA")
        ) {
          setReadinessMessage(
            "Finalizing private perps account. This can take a few seconds.",
          );
        } else {
          setReadinessMessage(`Readiness check failed: ${msg}`);
        }
      } finally {
        setReadinessChecking(false);
      }
    },
    [
      wallet.signer,
      vaultAddress,
      perpsMarketProxy,
      ownershipCommitment,
      configuredPerpsAssetToken,
    ],
  );

  const refreshPerpsMetrics = useCallback(async () => {
    if (!wallet.signer?.provider) return;
    try {
      if (
        !ethers.isAddress(vaultAddress) ||
        !ethers.isAddress(perpsAssetAddress)
      ) {
        setVaultAssetBalanceRaw(null);
        setVaultAssetBalanceUsd(null);
        setHasActiveTrade(false);
        setActiveTradeSummary(null);
        setPendingMarketOrderIds([]);
        setAvailableMarginUsd(null);
        setWithdrawableMarginUsd(null);
        setTotalCollateralUsd(null);
        setOpenPositionSize(null);
        setUnrealizedPnlUsd(null);
        return;
      }
      setMetricsLoading(true);
      const provider = wallet.signer.provider;
      const asset = new ethers.Contract(
        perpsAssetAddress,
        ["function balanceOf(address) view returns (uint256)"],
        provider,
      );
      const vaultBalanceRaw = BigInt(await asset.balanceOf(vaultAddress));
      setVaultAssetBalanceRaw(vaultBalanceRaw);
      const vaultBalanceUsd = toDecimalNumber(
        vaultBalanceRaw,
        perpsAssetDecimals,
      );
      setVaultAssetBalanceUsd(vaultBalanceUsd);
      setAvailableMarginUsd(vaultBalanceUsd);
      setWithdrawableMarginUsd(null);
      setTotalCollateralUsd(null);
      setOpenPositionSize(null);
      setUnrealizedPnlUsd(null);

      let activeTradeSummaryNext: AvantisActiveTradeSummary | null = null;
      let pendingOrderIds: string[] = [];
      const avantisPairIndex = resolveAvantisPairIndex(
        traderMarketId,
        selectedMarket.key,
      );
      if (
        ethers.isAddress(DEFAULT_TRADING_STORAGE_ADDRESS) &&
        Number.isFinite(Number(traderMarketId)) &&
        Number.isFinite(Number(accountId))
      ) {
        activeTradeSummaryNext = await readAvantisOpenTrade({
          provider,
          tradingStorageAddress: DEFAULT_TRADING_STORAGE_ADDRESS,
          vaultAddress,
          pairIndex: avantisPairIndex,
          tradeIndex: BigInt(accountId || "0"),
          collateralDecimals: perpsAssetDecimals,
        });
        try {
          pendingOrderIds = await readAvantisPendingOrderIds({
            provider,
            tradingStorageAddress: DEFAULT_TRADING_STORAGE_ADDRESS,
            vaultAddress,
          });
        } catch {
          pendingOrderIds = [];
        }
      }
      const activeTrade = !!activeTradeSummaryNext;
      setHasActiveTrade(activeTrade);
      setActiveTradeSummary(activeTradeSummaryNext);
      if (activeTradeSummaryNext) {
        setTotalCollateralUsd(activeTradeSummaryNext.collateralUsd);
        setOpenPositionSize(activeTradeSummaryNext.baseSize);
      }
      setPendingMarketOrderIds(pendingOrderIds);
    } catch (err) {
      addLog(
        `Perps metrics refresh failed: ${err instanceof Error ? err.message : String(err)}`,
        "warning",
      );
    } finally {
      setMetricsLoading(false);
    }
  }, [
    wallet.signer,
    accountId,
    traderMarketId,
    selectedMarket.key,
    addLog,
    vaultAddress,
    perpsAssetAddress,
    perpsAssetDecimals,
  ]);

  useEffect(() => {
    if (!wallet.signer || !vaultAddress) return;
    void refreshReadiness();
  }, [wallet.signer, vaultAddress, refreshReadiness]);

  useEffect(() => {
    if (!wallet.signer || !perpsMarketProxy || !accountId) return;
    void refreshPerpsMetrics();
    const interval = window.setInterval(() => {
      void refreshPerpsMetrics();
    }, 15_000);
    return () => {
      window.clearInterval(interval);
    };
  }, [
    wallet.signer,
    perpsMarketProxy,
    accountId,
    traderMarketId,
    refreshPerpsMetrics,
  ]);

  const resolveOwnershipWitness = useCallback(
    async (expectedOwnershipCommitment?: string) => {
      if (!wallet.spendingKeys)
        throw new Error(
          "Connect wallet to derive perps ownership proof witness",
        );
      const [publicKeyX, publicKeyY] = wallet.spendingKeys.publicKey;
      let saltHex = ownershipSalt;
      const normalizedExpectedCommitment = expectedOwnershipCommitment
        ? String(expectedOwnershipCommitment).trim().toLowerCase()
        : "";
      if (!saltHex) {
        if (normalizedExpectedCommitment) {
          throw new Error(
            "Missing perps ownership salt for this vault. Re-import or recreate the perps profile before signing proof-gated actions.",
          );
        }
        saltHex = await randomBytes32Hex();
        setOwnershipSalt(saltHex);
      }
      const computedCommitment = await computePerpsOwnershipCommitment(
        publicKeyX,
        publicKeyY,
        saltHex,
      );
      if (
        normalizedExpectedCommitment &&
        normalizedExpectedCommitment !== computedCommitment.toLowerCase()
      ) {
        throw new Error(
          "Stored perps ownership witness does not match the onchain vault commitment. Re-import or recreate the perps profile before signing proof-gated actions.",
        );
      }
      if (
        ownershipCommitment &&
        ownershipCommitment.toLowerCase() !== computedCommitment.toLowerCase()
      ) {
        throw new Error(
          "Stored ownership commitment does not match current wallet witness.",
        );
      }
      if (!ownershipCommitment) setOwnershipCommitment(computedCommitment);
      return {
        publicKeyX,
        publicKeyY,
        saltHex,
        ownershipCommitment:
          normalizedExpectedCommitment ||
          ownershipCommitment ||
          computedCommitment,
      };
    },
    [wallet.spendingKeys, ownershipSalt, ownershipCommitment],
  );

  const resolvePerpsActionCallValue = useCallback(
    async (domain: string): Promise<bigint> => {
      if (
        domain !== PERPS_ACTION_DOMAINS.commitOrder &&
        domain !== PERPS_ACTION_DOMAINS.marginDeposit &&
        domain !== PERPS_ACTION_DOMAINS.marginWithdraw
      ) {
        return 0n;
      }
      const config = await RelayerClient.getBasePerpsRelayConfig();
      return config.usesSynthetixOracle ? 0n : config.executionFeeWei;
    },
    [],
  );

  const generateActionProof = useCallback(
    async (
      domain: string,
      callData: string,
      expiryNum: number,
      marginReshieldInput?: {
        usdcAmount: bigint;
        outputCommitment: string;
        outputTokenHash: string;
        recoveryKey: string;
        encryptedNote: string;
      },
      nonceOffset = 0n,
      callValueWei = 0n,
    ) => {
      if (!wallet.signer?.provider)
        throw new Error("Wallet provider not available");
      if (!ethers.isAddress(vaultAddress))
        throw new Error("Invalid vault address");
      const provider = wallet.signer.provider;
      const vault = new ethers.Contract(
        vaultAddress,
        [
          "function actionNonce() view returns (uint256)",
          "function perpsMarketProxy() view returns (address)",
          "function shieldedPool() view returns (address)",
          "function collateralToken() view returns (address)",
          "function ownershipCommitment() view returns (bytes32)",
          "function ownershipVerifier() view returns (address)",
          "function hasCustodiedAccountNft() view returns (bool)",
          "function computeActionHash(bytes32 actionDomain, bytes32 actionDataHash, uint64 expiry, uint256 nonce) view returns (bytes32)",
        ],
        provider,
      );

      for (let attempt = 0; attempt < 2; attempt += 1) {
        const [
          nonceRaw,
          marketProxyRaw,
          shieldedPoolRaw,
          collateralTokenRaw,
          ownershipCommitmentRaw,
          ownershipVerifierRaw,
        ] = await Promise.all([
          vault.actionNonce(),
          vault.perpsMarketProxy(),
          vault.shieldedPool(),
          vault.collateralToken(),
          vault.ownershipCommitment(),
          vault.ownershipVerifier(),
        ]);
        const marketProxy = String(marketProxyRaw);
        const shieldedPoolFromVault = String(shieldedPoolRaw);
        const collateralTokenFromVault = String(collateralTokenRaw);
        const ownershipCommitmentFromVault = String(ownershipCommitmentRaw);
        const ownershipVerifierAddress = String(ownershipVerifierRaw);
        if (marketProxy.toLowerCase() !== perpsMarketProxy.toLowerCase()) {
          setPerpsMarketProxy(marketProxy);
        }
        if (
          ownershipCommitmentFromVault &&
          ownershipCommitmentFromVault !== ethers.ZeroHash &&
          ownershipCommitment.toLowerCase() !==
            ownershipCommitmentFromVault.toLowerCase()
        ) {
          setOwnershipCommitment(ownershipCommitmentFromVault);
        }
        const shieldedPoolAddress = ethers.isAddress(shieldedPoolFromVault)
          ? shieldedPoolFromVault
          : EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT || ethers.ZeroAddress;
        const assetTokenForReshield = ethers.isAddress(collateralTokenFromVault)
          ? collateralTokenFromVault
          : perpsAssetAddress;
        let omitCallValueFromActionData = false;
        try {
          await vault.hasCustodiedAccountNft();
        } catch {
          omitCallValueFromActionData = true;
        }
        const actionNonce = BigInt(nonceRaw) + nonceOffset;
        const actionDataHash = computePerpsProofActionDataHash({
          actionDomain: domain as any,
          perpsMarketProxy: marketProxy,
          callData,
          callValueWei,
          omitCallValueFromActionData,
          marginReshieldConfig:
            domain === PERPS_ACTION_DOMAINS.marginReshield &&
            marginReshieldInput
              ? {
                  shieldedPool: shieldedPoolAddress,
                  usdcToken: assetTokenForReshield,
                  usdcAmount: marginReshieldInput.usdcAmount,
                  outputCommitment: marginReshieldInput.outputCommitment,
                  outputTokenHash: marginReshieldInput.outputTokenHash,
                  recoveryKey: marginReshieldInput.recoveryKey,
                  encryptedNote: marginReshieldInput.encryptedNote,
                }
              : undefined,
        });
        const localActionHash = computePerpsProofActionHash({
          vaultAddress,
          chainId,
          actionDomain: domain as any,
          perpsMarketProxy: marketProxy,
          callData,
          callValueWei,
          omitCallValueFromActionData,
          nonce: actionNonce,
          expiry: expiryNum,
          marginReshieldConfig:
            domain === PERPS_ACTION_DOMAINS.marginReshield &&
            marginReshieldInput
              ? {
                  shieldedPool: shieldedPoolAddress,
                  usdcToken: assetTokenForReshield,
                  usdcAmount: marginReshieldInput.usdcAmount,
                  outputCommitment: marginReshieldInput.outputCommitment,
                  outputTokenHash: marginReshieldInput.outputTokenHash,
                  recoveryKey: marginReshieldInput.recoveryKey,
                  encryptedNote: marginReshieldInput.encryptedNote,
                }
              : undefined,
        });
        const canonicalActionHash = String(
          await vault.computeActionHash(
            domain,
            actionDataHash,
            expiryNum,
            actionNonce,
          ),
        );
        if (
          canonicalActionHash.toLowerCase() !== localActionHash.toLowerCase()
        ) {
          addLog(
            "Perps action hash drifted from the live vault computation. Using the vault's canonical hash.",
            "warning",
          );
        }
        const witness = await resolveOwnershipWitness(
          ownershipCommitmentFromVault,
        );
        if (!wallet.spendingKeys?.spendingKey) {
          throw new Error(
            "Unlock the wallet to authorise this perps action: signing the action digest needs the spending key, which stays in the browser.",
          );
        }
        // Signed here, in the browser: the prover receives the signature over
        // this one action digest, never the secret that produced it.
        const signature = await signPerpsActionHash(
          wallet.spendingKeys.spendingKey,
          canonicalActionHash,
        );
        const proof = await generatePerpsOwnershipProof({
          chainId,
          publicKeyX: witness.publicKeyX.toString(),
          publicKeyY: witness.publicKeyY.toString(),
          salt: witness.saltHex,
          ownershipCommitment: witness.ownershipCommitment,
          actionHash: canonicalActionHash,
          signature,
        });
        const verifier = new ethers.Contract(
          ownershipVerifierAddress,
          [
            "function verifyOwnership(bytes32 ownershipCommitment, bytes32 actionHash, bytes ownershipProof) view returns (bool)",
          ],
          provider,
        );
        const proofValid = await verifier.verifyOwnership(
          ownershipCommitmentFromVault,
          canonicalActionHash,
          proof,
        );
        if (proofValid) {
          setOwnershipProof(proof);
          return proof;
        }
        if (attempt === 0) {
          addLog(
            "Perps ownership proof did not verify against the live vault. Refreshing vault state and retrying once.",
            "warning",
          );
          continue;
        }
        throw new Error(
          `Perps ownership proof failed live vault verification for ${shortAddress(vaultAddress)} (nonce ${actionNonce.toString()}). Refresh the perps account state and retry.`,
        );
      }
      throw new Error("Perps ownership proof generation exhausted retries");
    },
    [
      wallet.signer,
      vaultAddress,
      perpsMarketProxy,
      chainId,
      resolveOwnershipWitness,
      perpsAssetAddress,
      ownershipCommitment,
      addLog,
    ],
  );

  const handleCreateAccount = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    if (hasPerpsAccount) {
      setStatus({
        type: "info",
        message:
          "Perps account already exists. Refresh readiness to re-check it.",
      });
      return;
    }
    setPendingAction("createAccount");
    try {
      const witness = await resolveOwnershipWitness();
      const tokenIdNum = Number(accountTokenId);
      const accountIdNum = Number(accountId);
      const result = await createPerpsAccount({
        vaultOwner,
        accountNft: accountNft.trim() || undefined,
        accountTokenId:
          Number.isFinite(tokenIdNum) && tokenIdNum >= 0
            ? tokenIdNum
            : undefined,
        accountId:
          Number.isFinite(accountIdNum) && accountIdNum >= 0
            ? accountIdNum
            : undefined,
        permissionManager: permissionManager.trim() || undefined,
        ownershipVerifier,
        ownershipCommitment: witness.ownershipCommitment,
        perpsMarketProxy,
        autoCreateAccount: true,
      });
      if (!result.success) {
        setStatus({
          type: "error",
          message: result.error || "Perps account creation failed",
        });
        return;
      }
      if (result.ownershipCommitment)
        setOwnershipCommitment(result.ownershipCommitment);
      if (result.vaultAddress) setVaultAddress(result.vaultAddress);
      if (result.accountNft) setAccountNft(result.accountNft);
      if (result.accountTokenId) setAccountTokenId(result.accountTokenId);
      if (result.accountId) setAccountId(result.accountId);
      setStatus({
        type: "success",
        message: "Perps account creation submitted via relayer",
      });
      void refreshReadiness({
        vaultAddress: result.vaultAddress || vaultAddress,
        ownershipCommitment:
          result.ownershipCommitment || witness.ownershipCommitment,
        perpsMarketProxy,
      });
      void refreshPerpsMetrics();
    } catch (err) {
      setStatus({
        type: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    resolveOwnershipWitness,
    accountTokenId,
    accountId,
    createPerpsAccount,
    hasPerpsAccount,
    vaultOwner,
    accountNft,
    permissionManager,
    ownershipVerifier,
    perpsMarketProxy,
    setStatus,
    refreshReadiness,
    refreshPerpsMetrics,
  ]);

  const normalizePerpsErrorMessage = useCallback(
    (message: string) => {
      const lower = message.toLowerCase();
      if (message.includes("NO_TRADE")) {
        return "Open a position first, then use Add Margin.";
      }
      if (message.includes("BELOW_MIN_POS")) {
        return `Position size is below Avantis minimum. Increase order value (or leverage) so notional is at least $${AVANTIS_MIN_POSITION_NOTIONAL_USD.toFixed(2)}.`;
      }
      if (message.includes("0x350c20f1")) {
        return "Perps target rejected the action. Check trade params (especially minimum initial collateral) and retry.";
      }
      if (message.includes("0x90b8ec18") || lower.includes("transferfailed")) {
        return `Vault ${perpsAssetSymbol} transfer failed during private reshield. Refresh vault balances and retry with a lower amount; no funds were withdrawn.`;
      }
      return message;
    },
    [perpsAssetSymbol],
  );

  const handleFundVault = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("fundVault");
    try {
      if (!fundVaultFromPrivateAsset || !activePerpsAssetToken) {
        throw new Error("Vault funding function is not available");
      }
      if (!ethers.isAddress(vaultAddress)) {
        throw new Error("Invalid vault address");
      }
      const requestedAssetRaw = ethers.parseUnits(
        (traderMarginAmount || "0").trim(),
        perpsAssetDecimals,
      );
      if (requestedAssetRaw <= 0n) {
        throw new Error("Funding amount must be greater than zero");
      }
      const totalWithdrawFeeBps = BigInt(
        Math.max(
          0,
          Number(EVM_CONFIG.PROTOCOL_FEE_BPS || 0) +
            Number(EVM_CONFIG.PARTNER_FEE_BPS || 0),
        ),
      );
      const grossTopUpRaw = computeGrossTopUpForNet(
        requestedAssetRaw,
        totalWithdrawFeeBps,
      );
      addLog(
        `Perps vault funding: moving ${ethers.formatUnits(grossTopUpRaw, perpsAssetDecimals)} ${perpsAssetSymbol} gross from private balance`,
        "info",
      );
      const funded = await fundVaultFromPrivateAsset({
        vaultAddress,
        amountRaw: grossTopUpRaw,
        token: activePerpsAssetToken,
      });
      if (!funded) {
        throw new Error(
          `Funding vault from private ${perpsAssetSymbol} failed`,
        );
      }
      setStatus({
        type: "success",
        message: `Vault funded from private ${perpsAssetSymbol}`,
      });
      void refreshPerpsMetrics();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ type: "error", message: normalizePerpsErrorMessage(msg) });
    } finally {
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    fundVaultFromPrivateAsset,
    activePerpsAssetToken,
    vaultAddress,
    traderMarginAmount,
    addLog,
    setStatus,
    refreshPerpsMetrics,
    normalizePerpsErrorMessage,
    perpsAssetDecimals,
    perpsAssetSymbol,
  ]);

  const handleDepositMargin = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("depositMargin");
    try {
      if (!hasActiveTrade) {
        throw new Error("NO_TRADE");
      }
      if (perpsSelectorSupport?.updateMargin === false) {
        throw new Error(
          "This vault was created before Avantis margin updates were enabled.",
        );
      }
      const expiryNum = Number(expiry);
      const requestedAssetRaw = ethers.parseUnits(
        (traderMarginAmount || "0").trim(),
        perpsAssetDecimals,
      );
      if (requestedAssetRaw <= 0n) {
        throw new Error("Margin amount must be greater than zero");
      }
      if (!ethers.isAddress(vaultAddress)) {
        throw new Error("Invalid vault address");
      }
      if (!ethers.isAddress(perpsAssetAddress)) {
        throw new Error("Invalid perps asset token address configuration");
      }
      if (!wallet.signer?.provider) {
        throw new Error("Wallet provider not available");
      }
      const asset = new ethers.Contract(
        perpsAssetAddress,
        ["function balanceOf(address) view returns (uint256)"],
        wallet.signer.provider,
      );
      const vaultAssetBalance = BigInt(await asset.balanceOf(vaultAddress));
      if (vaultAssetBalance < requestedAssetRaw) {
        throw new Error(
          `Insufficient vault ${perpsAssetSymbol} (${ethers.formatUnits(vaultAssetBalance, perpsAssetDecimals)}). Fund vault before adding margin.`,
        );
      }
      const depositUsdcRaw = requestedAssetRaw;
      const callData = encodeAvantisUpdateMarginCallData({
        pairIndex: resolveAvantisPairIndex(traderMarketId, selectedMarket.key),
        tradeIndex: BigInt(accountId || "0"),
        updateType: 0,
        collateralChangeUsdc: depositUsdcRaw,
        priceUpdateData: [],
        priceSourcing: 0,
      });
      const callValueWei = await resolvePerpsActionCallValue(
        PERPS_ACTION_DOMAINS.marginDeposit,
      );
      const proof = await generateActionProof(
        PERPS_ACTION_DOMAINS.marginDeposit,
        callData,
        expiryNum,
        undefined,
        0n,
        callValueWei,
      );
      const result = await submitPerpsMargin({
        vaultAddress,
        userAddress: wallet.address,
        direction: "deposit",
        modifyCollateralCallData: callData,
        feeCollateralToken: perpsAssetAddress,
        expiry: expiryNum,
        ownershipProof: proof,
      });
      setStatus(
        result.success
          ? { type: "success", message: "Perps margin deposit submitted" }
          : {
              type: "error",
              message: result.error || "Perps margin deposit failed",
            },
      );
      if (result.success) void refreshPerpsMetrics();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ type: "error", message: normalizePerpsErrorMessage(msg) });
    } finally {
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    hasActiveTrade,
    expiry,
    accountId,
    traderMarketId,
    traderMarginAmount,
    resolvePerpsActionCallValue,
    generateActionProof,
    submitPerpsMargin,
    vaultAddress,
    selectedMarket.key,
    setStatus,
    wallet.address,
    wallet.signer,
    refreshPerpsMetrics,
    normalizePerpsErrorMessage,
    perpsAssetAddress,
    perpsAssetDecimals,
    perpsAssetSymbol,
    perpsSelectorSupport,
  ]);

  const handleWithdrawMargin = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("withdrawMargin");
    try {
      if (!hasActiveTrade) {
        throw new Error("NO_TRADE");
      }
      if (perpsSelectorSupport?.updateMargin === false) {
        throw new Error(
          "This vault was created before Avantis margin updates were enabled.",
        );
      }
      const expiryNum = Number(expiry);
      const marginAmountInput = (traderMarginAmount || "0").trim();
      const withdrawAmountRaw = ethers.parseUnits(
        marginAmountInput,
        perpsAssetDecimals,
      );
      const expectedReshieldAssetRaw = ethers.parseUnits(
        marginAmountInput,
        perpsAssetDecimals,
      );
      if (withdrawAmountRaw <= 0n) {
        throw new Error("Margin amount must be greater than zero");
      }
      if (!ethers.isAddress(vaultAddress)) {
        throw new Error("Invalid vault address");
      }
      if (!ethers.isAddress(wallet.address)) {
        throw new Error("Invalid wallet address for withdraw recipient");
      }
      let reshieldNote:
        | {
            random: bigint;
            npk: bigint;
            tokenHash: bigint;
            commitmentHex: string;
            recoveryKey: string;
            encryptedNote: string;
          }
        | undefined;
      if (ingestPerpsWithdrawPrivateNote && wallet.spendingKeys) {
        const randomBytes = ethers.randomBytes(32);
        const randomHex = ethers.hexlify(randomBytes);
        const randomBigInt = BigInt(randomHex);
        const recipientMpk =
          typeof wallet.spendingKeys.mpk === "bigint"
            ? wallet.spendingKeys.mpk
            : null;
        if (!recipientMpk) {
          throw new Error("Missing wallet MPK for private reshield");
        }
        const npk = await computeNPK(recipientMpk, randomBigInt);
        const tokenHash = await deriveTokenHash(perpsAssetAddress, "erc20");
        const outputCommitment = await computeCommitment(
          npk,
          tokenHash,
          expectedReshieldAssetRaw,
        );
        const commitmentHex = outputCommitment.startsWith("0x")
          ? outputCommitment
          : `0x${outputCommitment}`;
        if (!wallet.spendingKeys.spendingKey) {
          throw new Error(
            "Missing wallet inbox public key for private reshield",
          );
        }
        const recoveryKey = await deriveRecoveryKeyFromMpkHex(
          `0x${recipientMpk.toString(16).padStart(64, "0")}`,
          commitmentHex,
        );
        const encryptedNote = await buildRecoveryNotePayload(
          {
            npk,
            random: randomBigInt,
            tokenHash,
            value: expectedReshieldAssetRaw,
            commitment: commitmentHex,
            leafIndex: -1,
          },
          formatInboxPublicKeyHex(wallet.spendingKeys.spendingKey),
        );
        reshieldNote = {
          random: randomBigInt,
          npk,
          tokenHash,
          commitmentHex,
          recoveryKey,
          encryptedNote,
        };
      }
      const callData = encodeAvantisUpdateMarginCallData({
        pairIndex: resolveAvantisPairIndex(traderMarketId, selectedMarket.key),
        tradeIndex: BigInt(accountId || "0"),
        updateType: 1,
        collateralChangeUsdc: withdrawAmountRaw,
        priceUpdateData: [],
        priceSourcing: 0,
      });
      const withdrawProofRecipient = reshieldNote
        ? vaultAddress
        : wallet.address;
      const callValueWei = await resolvePerpsActionCallValue(
        PERPS_ACTION_DOMAINS.marginWithdraw,
      );
      const proof = await generateActionProof(
        PERPS_ACTION_DOMAINS.marginWithdraw,
        callData,
        expiryNum,
        undefined,
        0n,
        callValueWei,
      );
      const reshieldOwnershipProof = reshieldNote
        ? await generateActionProof(
            PERPS_ACTION_DOMAINS.marginReshield,
            callData,
            expiryNum,
            {
              usdcAmount: expectedReshieldAssetRaw,
              outputCommitment: reshieldNote.commitmentHex,
              outputTokenHash: `0x${reshieldNote.tokenHash.toString(16).padStart(64, "0")}`,
              recoveryKey: reshieldNote.recoveryKey,
              encryptedNote: reshieldNote.encryptedNote,
            },
            1n,
          )
        : undefined;
      const result = await submitPerpsMargin({
        vaultAddress,
        userAddress: wallet.address,
        direction: "withdraw",
        modifyCollateralCallData: callData,
        withdrawRecipient: withdrawProofRecipient,
        spotSynthMarketId: 0,
        privateReshield: !!reshieldNote,
        outputNpk: reshieldNote
          ? `0x${reshieldNote.npk.toString(16).padStart(64, "0")}`
          : undefined,
        outputTokenHash: reshieldNote
          ? `0x${reshieldNote.tokenHash.toString(16).padStart(64, "0")}`
          : undefined,
        outputCommitment: reshieldNote?.commitmentHex,
        outputRecoveryKey: reshieldNote?.recoveryKey,
        outputEncryptedNote: reshieldNote?.encryptedNote,
        expectedOutputAmount: reshieldNote
          ? expectedReshieldAssetRaw.toString()
          : undefined,
        reshieldOwnershipProof,
        expiry: expiryNum,
        ownershipProof: proof,
        feeCollateralToken: perpsAssetAddress,
      });
      if (!result.success && result.code === "PERPS_RESHIELD_REQUIRED") {
        addLog(
          `Perps withdraw confirmed${result.withdrawTxHash ? ` (${result.withdrawTxHash})` : ""}; private reshield needs vault-withdraw recovery.`,
          "warning",
          result.withdrawTxHash || result.txHash,
        );
        setStatus({
          type: "info",
          message: `Withdraw succeeded; use Vault Withdraw to reshield ${perpsAssetSymbol} from the vault.`,
        });
        void refreshPerpsMetrics();
        return;
      }
      if (result.success && reshieldNote && ingestPerpsWithdrawPrivateNote) {
        if (
          result.reshielded !== true ||
          !result.depositTxHash ||
          !result.outputAmount ||
          typeof result.leafIndex !== "number" ||
          result.leafIndex < 0
        ) {
          throw new Error(
            "Private reshield is not confirmed onchain yet. Private balance will update after the deposit transaction confirms.",
          );
        }
        const outputAmountRaw = BigInt(result.outputAmount);
        const note: JoinSplitNote = {
          npk: reshieldNote.npk,
          random: reshieldNote.random,
          commitment: result.outputCommitment || reshieldNote.commitmentHex,
          tokenHash: reshieldNote.tokenHash,
          value: outputAmountRaw,
          leafIndex: result.leafIndex,
        };
        const ingested = await ingestPerpsWithdrawPrivateNote({
          note,
          txHash: result.depositTxHash,
          amountRaw: outputAmountRaw,
          token: activePerpsAssetToken!,
        });
        if (!ingested) {
          throw new Error(
            "Withdraw succeeded but private note ingestion failed",
          );
        }
      } else if (
        result.success &&
        shieldWalletAssetToPrivate &&
        activePerpsAssetToken
      ) {
        let shieldAmountRaw = withdrawAmountRaw;
        if (result.txHash && wallet.signer?.provider) {
          const receipt = await wallet.signer.provider.getTransactionReceipt(
            result.txHash,
          );
          if (receipt) {
            const iface = new ethers.Interface([
              "event MarginWithdrawnToUSDC(address indexed recipient,uint256 sUSDAmount,uint256 usdcAmount,uint256 nonce)",
            ]);
            for (const log of receipt.logs) {
              if (
                String(log.address).toLowerCase() !== vaultAddress.toLowerCase()
              )
                continue;
              try {
                const parsed = iface.parseLog({
                  topics: Array.from(log.topics),
                  data: String(log.data),
                });
                if (parsed.name !== "MarginWithdrawnToUSDC") continue;
                const recipientFromLog = String(
                  parsed.args.recipient,
                ).toLowerCase();
                if (recipientFromLog !== wallet.address.toLowerCase()) continue;
                shieldAmountRaw = BigInt(parsed.args.usdcAmount);
                break;
              } catch {
                // ignore unrelated logs
              }
            }
          }
        }
        addLog(
          `Perps margin auto-shield: moving withdrawn ${perpsAssetSymbol} back to private balance`,
          "info",
        );
        const shielded = await shieldWalletAssetToPrivate({
          amountRaw: shieldAmountRaw,
          token: activePerpsAssetToken,
        });
        if (!shielded) {
          throw new Error(
            "Withdraw succeeded but auto-shield to private balance failed",
          );
        }
      } else if (result.success) {
        addLog(
          `Perps withdraw sent ${perpsAssetSymbol} to wallet. Shield manually from the Add tab.`,
          "info",
        );
      }
      setStatus(
        result.success
          ? { type: "success", message: "Perps margin withdraw submitted" }
          : {
              type: "error",
              message: result.error || "Perps margin withdraw failed",
            },
      );
      if (result.success) void refreshPerpsMetrics();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ type: "error", message: normalizePerpsErrorMessage(msg) });
    } finally {
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    hasActiveTrade,
    expiry,
    accountId,
    traderMarketId,
    traderMarginAmount,
    selectedMarket.key,
    resolvePerpsActionCallValue,
    generateActionProof,
    submitPerpsMargin,
    vaultAddress,
    setStatus,
    wallet.address,
    wallet.spendingKeys,
    shieldWalletAssetToPrivate,
    ingestPerpsWithdrawPrivateNote,
    addLog,
    refreshPerpsMetrics,
    normalizePerpsErrorMessage,
    perpsAssetAddress,
    perpsAssetDecimals,
    perpsAssetSymbol,
    activePerpsAssetToken,
    wallet.signer,
    perpsSelectorSupport,
  ]);

  const handleWithdrawVault = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("withdrawVault");
    try {
      const expiryNum = Number(expiry);
      if (!ethers.isAddress(vaultAddress)) {
        throw new Error("Invalid vault address");
      }
      const amountInput = (traderMarginAmount || "0").trim();
      let withdrawAmountRaw: bigint;
      try {
        withdrawAmountRaw = ethers.parseUnits(amountInput, perpsAssetDecimals);
      } catch {
        throw new Error(`Enter a valid ${perpsAssetSymbol} amount to withdraw`);
      }
      if (withdrawAmountRaw <= 0n) {
        throw new Error("Withdraw amount must be greater than zero");
      }
      if (!ethers.isAddress(perpsAssetAddress)) {
        throw new Error("Invalid perps asset token address configuration");
      }
      if (!wallet.signer?.provider) {
        throw new Error("Wallet provider not available");
      }
      const asset = new ethers.Contract(
        perpsAssetAddress,
        ["function balanceOf(address) view returns (uint256)"],
        wallet.signer.provider,
      );
      const vaultBalanceRaw = BigInt(await asset.balanceOf(vaultAddress));
      setVaultAssetBalanceRaw(vaultBalanceRaw);
      const vaultBalanceUsd = toDecimalNumber(
        vaultBalanceRaw,
        perpsAssetDecimals,
      );
      setVaultAssetBalanceUsd(vaultBalanceUsd);
      setAvailableMarginUsd(vaultBalanceUsd);
      if (withdrawAmountRaw > vaultBalanceRaw) {
        throw new Error(
          buildInsufficientVaultAssetMessage({
            requestedRaw: withdrawAmountRaw,
            availableRaw: vaultBalanceRaw,
            decimals: perpsAssetDecimals,
            symbol: perpsAssetSymbol,
          }),
        );
      }
      if (!wallet.spendingKeys) {
        throw new Error("Missing wallet spending keys for private reshield");
      }
      const randomBytes = ethers.randomBytes(32);
      const randomHex = ethers.hexlify(randomBytes);
      const randomBigInt = BigInt(randomHex);
      const recipientMpk =
        typeof wallet.spendingKeys.mpk === "bigint"
          ? wallet.spendingKeys.mpk
          : null;
      if (!recipientMpk) {
        throw new Error("Missing wallet MPK for private reshield");
      }
      const npk = await computeNPK(recipientMpk, randomBigInt);
      const tokenHash = await deriveTokenHash(perpsAssetAddress, "erc20");
      const outputCommitment = await computeCommitment(
        npk,
        tokenHash,
        withdrawAmountRaw,
      );
      const commitmentHex = outputCommitment.startsWith("0x")
        ? outputCommitment
        : `0x${outputCommitment}`;
      if (!wallet.spendingKeys.spendingKey) {
        throw new Error("Missing wallet inbox public key for private reshield");
      }
      const recoveryKey = await deriveRecoveryKeyFromMpkHex(
        `0x${recipientMpk.toString(16).padStart(64, "0")}`,
        commitmentHex,
      );
      const encryptedNote = await buildRecoveryNotePayload(
        {
          npk,
          random: randomBigInt,
          tokenHash,
          value: withdrawAmountRaw,
          commitment: commitmentHex,
          leafIndex: -1,
        },
        formatInboxPublicKeyHex(wallet.spendingKeys.spendingKey),
      );
      setStatus({
        type: "info",
        message: "Generating vault withdraw authorization...",
      });
      const proof = await generateActionProof(
        PERPS_ACTION_DOMAINS.marginReshield,
        "0x",
        expiryNum,
        {
          usdcAmount: withdrawAmountRaw,
          outputCommitment: commitmentHex,
          outputTokenHash: `0x${tokenHash.toString(16).padStart(64, "0")}`,
          recoveryKey,
          encryptedNote,
        },
      );
      setStatus({
        type: "info",
        message: "Submitting vault withdraw to private balance...",
      });
      const result = await submitPerpsVaultWithdraw({
        vaultAddress,
        userAddress: wallet.address,
        feeCollateralToken: perpsAssetAddress,
        outputNpk: `0x${npk.toString(16).padStart(64, "0")}`,
        outputTokenHash: `0x${tokenHash.toString(16).padStart(64, "0")}`,
        outputCommitment: commitmentHex,
        outputRecoveryKey: recoveryKey,
        outputEncryptedNote: encryptedNote,
        expectedOutputAmount: withdrawAmountRaw.toString(),
        expiry: expiryNum,
        ownershipProof: proof,
      });
      if (result.success && ingestPerpsWithdrawPrivateNote) {
        if (
          result.reshielded !== true ||
          !result.depositTxHash ||
          !result.outputAmount ||
          typeof result.leafIndex !== "number" ||
          result.leafIndex < 0
        ) {
          throw new Error(
            "Vault withdraw is not confirmed onchain yet. Private balance will update after the deposit transaction confirms.",
          );
        }
        const outputAmountRaw = BigInt(result.outputAmount);
        const note: JoinSplitNote = {
          npk,
          random: randomBigInt,
          commitment: result.outputCommitment || commitmentHex,
          tokenHash,
          value: outputAmountRaw,
          leafIndex: result.leafIndex,
        };
        const ingested = await ingestPerpsWithdrawPrivateNote({
          note,
          txHash: result.depositTxHash,
          amountRaw: outputAmountRaw,
          token: activePerpsAssetToken!,
        });
        if (!ingested) {
          throw new Error(
            "Vault withdraw succeeded but private note ingestion failed",
          );
        }
      }
      setStatus(
        result.success
          ? {
              type: "success",
              message: "Vault withdraw submitted to private balance",
            }
          : { type: "error", message: result.error || "Vault withdraw failed" },
      );
      if (result.success) {
        if (vaultAssetBalanceRaw != null) {
          const nextVaultBalanceRaw =
            vaultAssetBalanceRaw > withdrawAmountRaw
              ? vaultAssetBalanceRaw - withdrawAmountRaw
              : 0n;
          setVaultAssetBalanceRaw(nextVaultBalanceRaw);
          const nextVaultBalanceUsd = toDecimalNumber(
            nextVaultBalanceRaw,
            perpsAssetDecimals,
          );
          setVaultAssetBalanceUsd(nextVaultBalanceUsd);
          setAvailableMarginUsd(nextVaultBalanceUsd);
        }
        await refreshPerpsMetrics();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ type: "error", message: normalizePerpsErrorMessage(msg) });
      void refreshPerpsMetrics();
    } finally {
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    expiry,
    vaultAddress,
    traderMarginAmount,
    wallet.address,
    wallet.spendingKeys,
    generateActionProof,
    submitPerpsVaultWithdraw,
    ingestPerpsWithdrawPrivateNote,
    setStatus,
    refreshPerpsMetrics,
    normalizePerpsErrorMessage,
    perpsAssetAddress,
    perpsAssetDecimals,
    perpsAssetSymbol,
    activePerpsAssetToken,
    wallet.signer,
    vaultAssetBalanceRaw,
  ]);

  const monitorAvantisOrderSettlement = useCallback(
    async (txHash: string) => {
      const provider = wallet.signer?.provider;
      if (
        !provider ||
        !ethers.isAddress(vaultAddress) ||
        !ethers.isAddress(DEFAULT_TRADING_STORAGE_ADDRESS)
      ) {
        return;
      }

      try {
        let receipt = await provider.getTransactionReceipt(txHash);
        if (!receipt) {
          receipt = await provider.waitForTransaction(txHash, 1, 45_000);
        }
        if (!receipt || receipt.status !== 1) {
          await refreshPerpsMetrics();
          return;
        }

        const initiatedOrder = getInitiatedAvantisOrder(receipt, vaultAddress);
        if (!initiatedOrder) {
          addLog(
            "Perps commit relayed, but no Avantis market-order id was found in the receipt.",
            "warning",
            txHash,
          );
          await refreshPerpsMetrics();
          return;
        }

        const tradeIndex = BigInt(accountId || "0");
        setPendingMarketOrderIds((current) =>
          current.includes(initiatedOrder.orderId)
            ? current
            : [initiatedOrder.orderId, ...current],
        );
        const orderKind = initiatedOrder.isOpen ? "order" : "close order";
        setStatus({
          type: "info",
          message: `Avantis ${orderKind} #${initiatedOrder.orderId} submitted. Waiting for execution...`,
        });
        addLog(
          `Avantis market ${orderKind} #${initiatedOrder.orderId} submitted from vault ${shortAddress(vaultAddress)}`,
          "info",
          txHash,
        );

        const deadlineMs = Date.now() + 75_000;
        let fromBlock = receipt.blockNumber;
        while (Date.now() < deadlineMs) {
          await waitMs(3_000);

          const latestBlock = await provider.getBlockNumber();
          if (fromBlock <= latestBlock) {
            const cancelLogs = await provider.getLogs({
              address: DEFAULT_TRADING_STORAGE_ADDRESS,
              fromBlock,
              toBlock: latestBlock,
              topics: [
                AVANTIS_MARKET_OPEN_CANCELED_TOPIC,
                addressLogTopic(vaultAddress),
              ],
            });
            for (const log of cancelLogs) {
              const parsed = parseAvantisLog(log);
              if (
                parsed?.name === "MarketOpenCanceled" &&
                parsed.args[0].toString() === initiatedOrder.orderId
              ) {
                setPendingMarketOrderIds((current) =>
                  current.filter((id) => id !== initiatedOrder.orderId),
                );
                setStatus({
                  type: "info",
                  message: `Avantis canceled order #${initiatedOrder.orderId} and refunded the vault collateral. Refresh price and retry.`,
                });
                addLog(
                  `Avantis canceled order #${initiatedOrder.orderId}; vault collateral was refunded.`,
                  "warning",
                  log.transactionHash,
                );
                await refreshPerpsMetrics();
                return;
              }
            }
            fromBlock = latestBlock + 1;
          }

          const activeTrade = await readAvantisOpenTrade({
            provider,
            tradingStorageAddress: DEFAULT_TRADING_STORAGE_ADDRESS,
            vaultAddress,
            pairIndex: initiatedOrder.pairIndex,
            tradeIndex,
            collateralDecimals: perpsAssetDecimals,
          });
          if (
            (initiatedOrder.isOpen && activeTrade) ||
            (!initiatedOrder.isOpen && !activeTrade)
          ) {
            setPendingMarketOrderIds((current) =>
              current.filter((id) => id !== initiatedOrder.orderId),
            );
            setStatus({
              type: "success",
              message: initiatedOrder.isOpen
                ? `Avantis order #${initiatedOrder.orderId} executed. Position is active.`
                : `Avantis close order #${initiatedOrder.orderId} executed. Position is closed.`,
            });
            addLog(
              initiatedOrder.isOpen
                ? `Avantis order #${initiatedOrder.orderId} executed; position is active.`
                : `Avantis close order #${initiatedOrder.orderId} executed; position is closed.`,
              "success",
              txHash,
            );
            await refreshPerpsMetrics();
            return;
          }
        }

        setStatus({
          type: "info",
          message: `Avantis order #${initiatedOrder.orderId} is still pending. Refresh in a few seconds.`,
        });
        await refreshPerpsMetrics();
      } catch (err) {
        addLog(
          `Avantis order follow-up failed: ${err instanceof Error ? err.message : String(err)}`,
          "warning",
          txHash,
        );
        await refreshPerpsMetrics();
      }
    },
    [
      wallet.signer,
      vaultAddress,
      accountId,
      perpsAssetDecimals,
      addLog,
      refreshPerpsMetrics,
      setStatus,
    ],
  );

  const handleCommitOrder = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("commitOrder");
    try {
      if (hasActiveTrade) {
        throw new Error("Close the active position before opening another.");
      }
      const leverageNum = Number(orderLeverage || "0");
      if (!Number.isFinite(leverageNum) || leverageNum <= 0) {
        throw new Error("Enter a valid positive leverage before committing.");
      }
      const notionalUsd = Number(orderValueUsd || "0");
      if (!Number.isFinite(notionalUsd) || notionalUsd <= 0) {
        throw new Error(
          "Enter a valid positive order value before committing.",
        );
      }
      const leverageX10 = ethers.parseUnits(
        leverageNum.toFixed(AVANTIS_LEVERAGE_DECIMALS),
        AVANTIS_LEVERAGE_DECIMALS,
      );
      const collateralInTradeUsdcRaw = computeAvantisCollateralInTradeUsdcRaw({
        orderValueUsd,
        leverageX10,
        perpsAssetDecimals,
      });
      if (!collateralInTradeUsdcRaw || collateralInTradeUsdcRaw <= 0n) {
        throw new Error(
          "Enter a valid positive order value before committing.",
        );
      }
      if (!ethers.isAddress(vaultAddress)) {
        throw new Error("Invalid vault address");
      }
      if (!ethers.isAddress(perpsAssetAddress)) {
        throw new Error("Invalid perps asset token address configuration");
      }
      if (!wallet.signer?.provider) {
        throw new Error("Wallet provider not available");
      }
      if (perpsSelectorSupport?.openTrade === false) {
        throw new Error(
          "This vault is missing the Avantis open trade selector.",
        );
      }
      const openPriceNum = Number(orderPriceUsd.trim());
      if (!Number.isFinite(openPriceNum) || openPriceNum <= 0) {
        throw new Error("Enter a valid positive price before committing.");
      }
      const requiredMarginRaw = ethers.parseUnits(
        Math.max(derivedTrade.requiredMarginUsd, 0).toFixed(perpsAssetDecimals),
        perpsAssetDecimals,
      );
      const asset = new ethers.Contract(
        perpsAssetAddress,
        ["function balanceOf(address) view returns (uint256)"],
        wallet.signer.provider,
      );
      const liveVaultBalanceRaw = BigInt(await asset.balanceOf(vaultAddress));
      setVaultAssetBalanceRaw(liveVaultBalanceRaw);
      const liveVaultBalanceUsd = toDecimalNumber(
        liveVaultBalanceRaw,
        perpsAssetDecimals,
      );
      setVaultAssetBalanceUsd(liveVaultBalanceUsd);
      setAvailableMarginUsd(liveVaultBalanceUsd);
      const avantisPairIndex = resolveAvantisPairIndex(
        traderMarketId,
        selectedMarket.key,
      );
      const isLong = tradeDirection === "long";
      if (requiredMarginRaw > 0n && liveVaultBalanceRaw < requiredMarginRaw) {
        throw new Error(
          `Insufficient vault ${perpsAssetSymbol}: order requires ${ethers.formatUnits(requiredMarginRaw, perpsAssetDecimals)} ${perpsAssetSymbol}, available ${ethers.formatUnits(liveVaultBalanceRaw, perpsAssetDecimals)} ${perpsAssetSymbol}. Fund vault or reduce notional before committing.`,
        );
      }
      if (collateralInTradeUsdcRaw < AVANTIS_MIN_INITIAL_COLLATERAL_USDC_RAW) {
        throw new Error(
          `Initial collateral is too low for Avantis. Required minimum is ${ethers.formatUnits(AVANTIS_MIN_INITIAL_COLLATERAL_USDC_RAW, perpsAssetDecimals)} ${perpsAssetSymbol}.`,
        );
      }
      if (
        !Number.isFinite(notionalUsd) ||
        notionalUsd + 1e-9 < AVANTIS_MIN_POSITION_NOTIONAL_USD
      ) {
        throw new Error(
          `Position notional is too low for Avantis. Required minimum is $${AVANTIS_MIN_POSITION_NOTIONAL_USD.toFixed(2)}.`,
        );
      }
      const expiryNum = Number(expiry);
      const callData = (() => {
        return encodeAvantisOpenTradeCallData({
          trader: vaultAddress,
          pairIndex: avantisPairIndex,
          tradeIndex: BigInt(accountId || "0"),
          collateralInTradeUsdc: collateralInTradeUsdcRaw,
          isLong,
          leverageX10,
          tpX10: 0n,
          slX10: 0n,
          openPriceX10: ethers.parseUnits(
            Math.max(openPriceNum, 1).toFixed(10),
            10,
          ),
          orderType: 0,
          slippagePX10: AVANTIS_DEFAULT_MARKET_SLIPPAGE_X10,
          timestamp: BigInt(Math.floor(Date.now() / 1000)),
        });
      })();
      const callValueWei = await resolvePerpsActionCallValue(
        PERPS_ACTION_DOMAINS.commitOrder,
      );
      const proof = await generateActionProof(
        PERPS_ACTION_DOMAINS.commitOrder,
        callData,
        expiryNum,
        undefined,
        0n,
        callValueWei,
      );
      const result = await submitPerpsTrade({
        vaultAddress,
        userAddress: wallet.address,
        action: "commit",
        actionCallData: callData,
        expiry: expiryNum,
        ownershipProof: proof,
      });
      setStatus(
        result.success
          ? { type: "success", message: "Perps order commit submitted" }
          : {
              type: "error",
              message: result.error || "Perps order commit failed",
            },
      );
      if (result.success) {
        if (result.txHash) {
          void monitorAvantisOrderSettlement(result.txHash);
        } else {
          void refreshPerpsMetrics();
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ type: "error", message: normalizePerpsErrorMessage(msg) });
    } finally {
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    derivedTrade.requiredMarginUsd,
    expiry,
    accountId,
    traderMarketId,
    selectedMarket.key,
    resolvePerpsActionCallValue,
    generateActionProof,
    submitPerpsTrade,
    vaultAddress,
    perpsAssetAddress,
    wallet.signer,
    wallet.address,
    setStatus,
    refreshPerpsMetrics,
    monitorAvantisOrderSettlement,
    orderPriceUsd,
    orderLeverage,
    orderValueUsd,
    tradeDirection,
    normalizePerpsErrorMessage,
    perpsAssetDecimals,
    perpsAssetSymbol,
    perpsSelectorSupport,
    hasActiveTrade,
  ]);

  const handleClosePosition = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("closePosition");
    try {
      if (!hasActiveTrade || !activeTradeSummary) {
        throw new Error("No active Avantis position to close.");
      }
      if (perpsSelectorSupport?.closeTradeMarket === false) {
        throw new Error(
          "This vault is missing the Avantis close-position selector.",
        );
      }
      if (!ethers.isAddress(vaultAddress)) {
        throw new Error("Invalid vault address");
      }
      const expiryNum = Number(expiry);
      const callData = encodeAvantisCloseTradeCallData({
        pairIndex: resolveAvantisPairIndex(traderMarketId, selectedMarket.key),
        tradeIndex: BigInt(accountId || "0"),
        collateralToCloseUsdc: activeTradeSummary.collateralRaw,
      });
      const callValueWei = await resolvePerpsActionCallValue(
        PERPS_ACTION_DOMAINS.commitOrder,
      );
      const proof = await generateActionProof(
        PERPS_ACTION_DOMAINS.commitOrder,
        callData,
        expiryNum,
        undefined,
        0n,
        callValueWei,
      );
      const result = await submitPerpsTrade({
        vaultAddress,
        userAddress: wallet.address,
        action: "commit",
        actionCallData: callData,
        expiry: expiryNum,
        ownershipProof: proof,
      });
      setStatus(
        result.success
          ? { type: "success", message: "Perps close submitted" }
          : {
              type: "error",
              message: result.error || "Perps close failed",
            },
      );
      if (result.success) {
        if (result.txHash) {
          void monitorAvantisOrderSettlement(result.txHash);
        } else {
          void refreshPerpsMetrics();
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus({ type: "error", message: normalizePerpsErrorMessage(msg) });
    } finally {
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    hasActiveTrade,
    activeTradeSummary,
    perpsSelectorSupport,
    vaultAddress,
    expiry,
    traderMarketId,
    selectedMarket.key,
    accountId,
    resolvePerpsActionCallValue,
    generateActionProof,
    submitPerpsTrade,
    wallet.address,
    setStatus,
    monitorAvantisOrderSettlement,
    refreshPerpsMetrics,
    normalizePerpsErrorMessage,
  ]);

  const handleCancelOrder = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("cancelOrder");
    try {
      if (perpsSelectorSupport?.cancelOpenLimitOrder === false) {
        throw new Error(
          "This vault was created before Avantis limit-order cancellation was enabled.",
        );
      }
      const expiryNum = Number(expiry);
      const callData = encodeAvantisCancelLimitOrderCallData({
        pairIndex: resolveAvantisPairIndex(traderMarketId, selectedMarket.key),
        tradeIndex: BigInt(accountId || "0"),
      });
      const proof = await generateActionProof(
        PERPS_ACTION_DOMAINS.cancelOrder,
        callData,
        expiryNum,
      );
      const result = await submitPerpsTrade({
        vaultAddress,
        userAddress: wallet.address,
        action: "cancel",
        actionCallData: callData,
        expiry: expiryNum,
        ownershipProof: proof,
      });
      setStatus(
        result.success
          ? { type: "success", message: "Perps order cancel submitted" }
          : {
              type: "error",
              message: result.error || "Perps order cancel failed",
            },
      );
      if (result.success) void refreshPerpsMetrics();
    } catch (err) {
      setStatus({
        type: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    expiry,
    accountId,
    traderMarketId,
    selectedMarket.key,
    generateActionProof,
    submitPerpsTrade,
    vaultAddress,
    wallet.address,
    setStatus,
    refreshPerpsMetrics,
    perpsSelectorSupport,
  ]);

  const applyRecommendedMargin = useCallback(() => {
    setTraderMarginAmount(derivedTrade.recommendedMarginInput);
  }, [derivedTrade.recommendedMarginInput]);

  const handleDelegate = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("delegate");
    try {
      setLoading(true);
      const result = await executePerpsDelegatePermission({
        chainId,
        vaultAddress,
        userAddress: wallet.address,
        executor,
        permission,
        expiry: Number(expiry),
        ownershipProof,
        onLog: addLog,
      });
      setStatus(
        result.success
          ? { type: "success", message: "Perps delegate submitted via relayer" }
          : { type: "error", message: result.error || "Perps delegate failed" },
      );
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    setLoading,
    chainId,
    vaultAddress,
    wallet.address,
    executor,
    permission,
    expiry,
    ownershipProof,
    addLog,
    setStatus,
  ]);

  const handleRevoke = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("revoke");
    try {
      setLoading(true);
      const result = await executePerpsRevokePermission({
        chainId,
        vaultAddress,
        userAddress: wallet.address,
        executor,
        permission,
        expiry: Number(expiry),
        ownershipProof,
        onLog: addLog,
      });
      setStatus(
        result.success
          ? { type: "success", message: "Perps revoke submitted via relayer" }
          : { type: "error", message: result.error || "Perps revoke failed" },
      );
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    setLoading,
    chainId,
    vaultAddress,
    wallet.address,
    executor,
    permission,
    expiry,
    ownershipProof,
    addLog,
    setStatus,
  ]);

  const handleEscape = useCallback(async () => {
    if (!ensureWalletCompliant()) return;
    setPendingAction("escape");
    try {
      setLoading(true);
      const result = await executePerpsEscapeTransfer({
        chainId,
        vaultAddress,
        recipient: escapeRecipient,
        expiry: Number(expiry),
        ownershipProof,
        onLog: addLog,
      });
      setStatus(
        result.success
          ? { type: "success", message: "Perps escape submitted via relayer" }
          : { type: "error", message: result.error || "Perps escape failed" },
      );
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  }, [
    ensureWalletCompliant,
    setLoading,
    chainId,
    vaultAddress,
    escapeRecipient,
    expiry,
    ownershipProof,
    addLog,
    setStatus,
  ]);

  return {
    vaultOwner,
    setVaultOwner,
    accountNft,
    setAccountNft,
    accountTokenId,
    setAccountTokenId,
    accountId,
    setAccountId,
    permissionManager,
    setPermissionManager,
    ownershipVerifier,
    setOwnershipVerifier,
    ownershipCommitment,
    setOwnershipCommitment,
    perpsMarketProxy,
    setPerpsMarketProxy,
    vaultAddress,
    setVaultAddress,
    executor,
    setExecutor,
    permission,
    setPermission,
    expiry,
    setExpiry,
    ownershipProof,
    setOwnershipProof,
    escapeRecipient,
    setEscapeRecipient,
    traderMarketId,
    setTraderMarketId,
    traderCollateralAddress,
    setTraderCollateralAddress,
    traderMarginAmount,
    setTraderMarginAmount,
    traderOrderSizeDelta,
    setTraderOrderSizeDelta,
    traderOrderSettlementStrategyId,
    setTraderOrderSettlementStrategyId,
    traderOrderAcceptablePrice,
    setTraderOrderAcceptablePrice,
    traderOrderTrackingCode,
    setTraderOrderTrackingCode,
    traderOrderReferrer,
    setTraderOrderReferrer,
    selectedMarket,
    availableMarkets: PERPS_MARKETS,
    selectedMarketKey,
    setSelectedMarketKey,
    tradeDirection,
    setTradeDirection,
    orderPriceUsd,
    setOrderPriceUsd,
    orderValueUsd,
    setOrderValueUsd,
    orderLeverage,
    setOrderLeverage,
    derivedTrade,
    chartEmbedUrl,
    hasPerpsAccount,
    ready,
    readinessChecking,
    readinessMessage,
    perpsSelectorSupport,
    metricsLoading,
    pendingAction,
    availableMarginUsd,
    vaultAssetBalanceUsd,
    vaultWithdrawPreflightMessage,
    perpsAssetSymbol,
    hasActiveTrade,
    activeTradeSummary,
    pendingMarketOrderIds,
    withdrawableMarginUsd,
    totalCollateralUsd,
    openPositionSize,
    unrealizedPnlUsd,
    refreshReadiness,
    refreshPerpsMetrics,
    handleCreateAccount,
    handleCommitOrder,
    handleClosePosition,
    handleCancelOrder,
    handleFundVault,
    handleWithdrawVault,
    handleDepositMargin,
    handleWithdrawMargin,
    applyRecommendedMargin,
    handleDelegate,
    handleRevoke,
    handleEscape,
  };
}
